// The Signing block of an onboarding case (SEP-293 section 11).
//
// IMs and EAs must not move a case to Signing unless:
//   - each required client document is opened and acknowledged at its
//     current version,
//   - the Internal Review is complete,
//   - each requirement of a Red Suitability flag is complete: the rationale
//     of the IM and the peer review.
//
// The server enforces the block in `OnboardingService.transition`, for each
// persona, and it records each blocked and each successful move. This module
// holds the wire shapes and the pure part that the server state machine and
// its web mirror both read: the Red-flag and the document conditions. Each
// mirror keeps its own checks of the fact find, the AML hand-over and the
// sign-off of the review, with its own words.

import { z } from 'zod';

import type { OnboardingPipelineCase } from './contracts/onboarding';
import { documentSigningBlockers } from './portal-document-acknowledgement';

/** Which condition raised a blocker. */
export const signingBlockerSourceSchema = z.enum([
    'documents',
    'internal_review',
    'red_flags',
    'gate',
]);
export type SigningBlockerSource = z.infer<typeof signingBlockerSourceSchema>;

/** One unmet condition of the move into Signing. */
export const signingBlockerSchema = z.object({
    source: signingBlockerSourceSchema,
    /** A stable code, for a test and for the audit. */
    code: z.string(),
    /** The sentence that the screen prints. */
    message: z.string(),
    /** What the blocker is about: the document labels, the flag ids. */
    subjects: z.array(
        z.object({
            kind: z.enum(['document', 'flag', 'review']),
            id: z.string(),
            label: z.string(),
        }),
    ),
});
export type SigningBlocker = z.infer<typeof signingBlockerSchema>;

/** The answer of `GET /api/onboarding/cases/:id/signing-readiness`. */
export const signingReadinessSchema = z.object({
    ready: z.boolean(),
    blockers: z.array(signingBlockerSchema),
    /** False while the feature of the portal acknowledgement is held. */
    documentsChecked: z.boolean(),
});
export type SigningReadiness = z.infer<typeof signingReadinessSchema>;

/**
 * The Red-flag condition and the document condition of the move into Signing,
 * from the derived fields of the case. `internalReview` carries the blockers
 * of the flag register (SEP-293 section 8), and `documentAcknowledgement`
 * carries the counts of the portal acknowledgement. A case with no
 * `documentAcknowledgement` (the feature is held) has no document condition.
 */
export function signingConditionBlockers(
    c: Pick<OnboardingPipelineCase, 'internalReview' | 'documentAcknowledgement'>,
): SigningBlocker[] {
    const out: SigningBlocker[] = [];
    for (const b of c.internalReview?.suitability.blockers ?? []) {
        out.push({
            source: 'red_flags',
            code: b.code,
            message: b.message,
            subjects: b.flagId ? [{ kind: 'flag', id: b.flagId, label: b.code }] : [],
        });
    }
    if (c.documentAcknowledgement) {
        for (const b of documentSigningBlockers(c.documentAcknowledgement)) {
            out.push({
                source: 'documents',
                code: b.code,
                message: b.message,
                subjects: b.documents.map((label) => ({ kind: 'document', id: label, label })),
            });
        }
    }
    return out;
}
